import React, { useCallback, useEffect, useState } from 'react';
import { StringService } from '@rocheglobal/component-library-dot-com/src/services/string/index';
import { TranslationService } from '@rocheglobal/component-library-dot-com/src/services/translation/index';
import { StoryblokComponent } from 'storyblok-js-client';
import { Default } from '../default';
import { blokToComponent, filterProps, slottedToComponents } from '../helpers';
import { AnyProps } from '../types';
import { DropdownOption, FormService } from '../../services';

interface ContactTranslations {
  region: string;
  subject: string;
}
interface NewsletterFormState {
  translations?: {
    firstNameLabel: string;
    lastNameLabel: string;
    emailLabel: string;
    consentLabel: string;
    inputError: string;
  };
}

enum ContactFormConditionalField {
  LABEL = 'occupation',
  VALUE = 'other',
  NAME = 'otherOccupation',
}

const RocheFormEmail = ({ blok, ...rest }: AnyProps): JSX.Element => Default({
  blok: {
    ...blok,
    component: 'roche-form',
    type: 'email',
  },
  ...rest,
});

const RocheFormContact = ({
  blok,
  getComponent,
  ...rest
}: AnyProps): JSX.Element => {
  const [regions, setRegions] = useState<DropdownOption[]>([]);
  const [subjects, setSubjects] = useState<DropdownOption[]>([]);
  const [translations, setTranslations] = useState<ContactTranslations>(null);
  const [isConditionalFieldVisible, setIsConditionalFieldVisible] = useState<boolean>(false);
  const [formElement, setFormElement] = useState<HTMLElement>(undefined);
  const measuredRef = useCallback((node: HTMLElement) => setFormElement(node), []);
  const [savedFormFieldValues, setSavedFormFieldValues] = useState<unknown>({});
  const formFieldValues = {};

  const contactFormConditionalField = {
    name: ContactFormConditionalField.NAME,
    type: 'text',
    label: StringService.capitalize(ContactFormConditionalField.VALUE),
    tooltip: '',
    required: isConditionalFieldVisible,
    component: 'roche-input',
  };

  useEffect(() => {
    const getTranslations = async (): Promise<void> => {
      const translationsContent: ContactTranslations = {
        region: await TranslationService.translate('region'),
        subject: await TranslationService.translate('subject'),
      };
      setTranslations(translationsContent);
    };
    const getFormData = async (): Promise<void> => {
      const { regions: allRegions, subjects: allSubjects } = await FormService.getContactFormData();
      setRegions(FormService.createOptions(['', ...allRegions]));
      setSubjects(FormService.createOptions(allSubjects));
    };
    getTranslations();
    getFormData();
  }, []);

  useEffect(() => {
    const handleInputChange = (event: CustomEvent) => {
      // TODO: replace this code when conditional fields are implemented (https://roche-ds.atlassian.net/browse/RCOM-691)
      formFieldValues[(event.target as Element).id] = event.detail;
      setSavedFormFieldValues(formFieldValues);
    };
    formElement?.addEventListener('rocheInputChange', handleInputChange);

    const handleDropdownChange = (event: CustomEvent) => {
      formFieldValues[(event.target as Element).id] = event.detail.index;
      setSavedFormFieldValues(formFieldValues);

      const { value: dropdownSelectedOption } = event.detail;
      const value = dropdownSelectedOption?.value.toLowerCase();
      const label = (event.target as HTMLOptionElement)?.label.toLowerCase();

      if (label !== ContactFormConditionalField.LABEL) {
        return;
      }

      setIsConditionalFieldVisible(
        label === ContactFormConditionalField.LABEL
        && value === ContactFormConditionalField.VALUE,
      );
    };
    formElement?.addEventListener('rocheDropdownChange', handleDropdownChange);

    return () => {
      formElement?.removeEventListener('rocheInputChange', handleInputChange);
      formElement?.removeEventListener('rocheDropdownChange', handleDropdownChange);
    };
  },
  [formElement]);

  if (!translations || regions.length <= 0 || subjects.length <= 0) {
    return null;
  }

  const { props, slotted } = filterProps(blok);

  const formProps = {
    ...props,
    // eslint-disable-next-line no-underscore-dangle
    ...(blok._uid ? { uid: blok._uid } : {}),
    // eslint-disable-next-line no-underscore-dangle
    ...(blok.id || blok._uid ? { id: blok.id || blok._uid } : {}),
    type: 'contact',
    ...rest,
  };

  const { ...restSlotted } = slotted;

  const formChildWithConditionalFieldInjected = {
    ...restSlotted,
    unnamed: [...restSlotted.unnamed as unknown[]].reduce(
      (accumulator: Record<string, unknown>[], value: Record<string, unknown>) => {
        const component = value;
        // eslint-disable-next-line no-underscore-dangle
        const savedFormFieldValuesIndex = savedFormFieldValues[component._uid as string];

        if (savedFormFieldValuesIndex && component.component === 'roche-input') {
          component.value = savedFormFieldValuesIndex;
        }
        if (savedFormFieldValuesIndex && component.component === 'roche-dropdown') {
          component.selected = savedFormFieldValuesIndex;
        }
        accumulator.push(component);
        if (
          (component?.label as string).toLowerCase() === ContactFormConditionalField.LABEL
          && isConditionalFieldVisible
        ) {
          accumulator.push(contactFormConditionalField);
        }
        return accumulator;
      },
      [],
    ),
  };

  const children = slottedToComponents(
    getComponent,
    formChildWithConditionalFieldInjected as Record<string, StoryblokComponent<string>[]>,
  );

  return (
    <roche-form
      {...formProps}
      ref={measuredRef}
    >
      <roche-dropdown
        name='region'
        label={StringService.capitalize(translations.region)}
        options={JSON.stringify(regions)}
        required
      />
      <roche-dropdown
        name='subject'
        label={StringService.capitalize(translations.subject)}
        options={JSON.stringify(subjects)}
        required
        placeholder=''
      />
      {children}
    </roche-form>
  );
};

const RocheFormNewsletter = ({
  blok,
  getComponent,
  ...rest
}: AnyProps): JSX.Element => {
  const { props, slotted } = filterProps(blok);

  const formProps = {
    ...props,
    // eslint-disable-next-line no-underscore-dangle
    ...(blok._uid ? { uid: blok._uid } : {}),
    // eslint-disable-next-line no-underscore-dangle
    ...(blok.id || blok._uid ? { id: blok.id || blok._uid } : {}),
    type: 'signup',
    ...rest,
  };
  const { consent, ...restSlotted } = slotted;
  const children = slottedToComponents(getComponent, restSlotted);
  const selectOptions = [{
    label: props['consent-label'],
    value: 'true',
    required: 'true',
  }];

  const [state, setState] = useState<NewsletterFormState>({});
  useEffect(() => {
    const getTranslations = async (): Promise<void> => {
      const newState: NewsletterFormState = {
        translations: {
          firstNameLabel: await TranslationService.translate('first name'),
          lastNameLabel: await TranslationService.translate('last name'),
          emailLabel: await TranslationService.translate('email'),
          consentLabel: await TranslationService.translate('consent'),
          inputError: await TranslationService.translate('invalid input'),
        },
      };
      setState(newState);
    };
    getTranslations();
  }, []);

  return (
    <roche-form {...formProps}>
      <roche-input error={state.translations?.inputError} type="text" name='firstname' label={state.translations?.firstNameLabel} required />
      <roche-input error={state.translations?.inputError} type="text" name='lastname' label={state.translations?.lastNameLabel} required />
      <roche-input error={state.translations?.inputError} type="email" name='email' label={state.translations?.emailLabel} required />
      {children}
      <roche-select multi-select name='consent' label={state.translations?.consentLabel} options={JSON.stringify(selectOptions)} required />
      {consent[0] && blokToComponent({ blok: slotted.consent[0], getComponent })}
    </roche-form>
  );
};

export const forms = {
  'roche-form-contact': RocheFormContact,
  'roche-form-email': RocheFormEmail,
  'roche-form-newsletter': RocheFormNewsletter,
  'roche-form': Default,
};
